import clsx from "clsx";
import React, { Fragment, useEffect } from "react";
import { useSelector } from "react-redux";
import CuratedProductsCard from "web/react/components/curated-products-card/curated-products-card";
import FeedNoResults from "web/react/components/feed-no-results/feed-no-results";
import { FeedsShowMore } from "web/react/components/feeds-show-more/feeds-show-more";
import { KeylineGrid, KeylineGridItem } from "web/react/components/keyline-grid";
import { OmnibusDirectiveBanner } from "web/react/components/omnibus-directive-banner/omnibus-directive-banner";
import ProductCard from "web/react/components/product-card/react/product-card";
import { useInitialReduxState } from "web/react/hooks/use-inital-redux-state/use-initial-redux-state";
import { setFeedData } from "web/redux/ducks/feed";
import { setAll as setShowMoreAll } from "web/redux/ducks/feed-show-more";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import globals from "web/script/modules/globals";
import { canUseMembership, shouldShowOmnibusPages } from "web/script/modules/utils";
import {
    CuratedProductsLinksSerializer,
    ProductCardSerializer,
    ProductFeedSerializer,
} from "web/types/serializers";
import styles from "./feed-grid.module.css";

export type FeedGridProps = {
    data: Pick<
        ProductFeedSerializer,
        "feed_items" | "type" | "pre_filters" | "curated_products" | "show_more_button"
    >;
};

export default function FeedGrid({ data }: FeedGridProps): JSX.Element | null {
    useInitialReduxState([
        [setFeedData, data],
        [setShowMoreAll, data.show_more_button],
    ]);

    if (!data.feed_items.length) {
        return <FeedNoResults />;
    }

    return (
        <>
            {shouldShowOmnibusPages() ? <OmnibusDirectiveBanner /> : null}
            <FeedGridConnected />
        </>
    );
}

function FeedGridConnected(): JSX.Element {
    const {
        data: { feed_items: feedItems, type, curated_products: curatedProducts },
        ui: { isFetching },
    } = useSelector((state: ReduxStoreState) => state.feed);

    return (
        <>
            <FeedGridInner
                feedItems={feedItems}
                curatedProducts={curatedProducts}
                isFetching={isFetching}
                feedType={type || ""}
            />
            <FeedsShowMore />
        </>
    );
}

interface FeedProductCardProps {
    product: ProductCardSerializer;
    feedType: string;
}

export function FeedProductCard({ product, feedType }: FeedProductCardProps): JSX.Element {
    // Please keep in mind that in addition to the below logic,
    // PDP Preview feature is shown on Mobile only, in place of `ProductCard.SaveForLaterButton`.
    // In order to hide `ProductCard.SaveForLaterButton` visually, we attach `hidden-mobile` className conditionally.
    // Initially we tried determining the Mobile viewport relying on `browser` and JS (instead of using CSS),
    // but it causes an extra render, because the Browser features are available after the component mounting.

    return (
        <ProductCard product={product} className={styles.product}>
            <ProductCard.Image width={200} height={250} feedType={feedType}>
                <ProductCard.Badges />
            </ProductCard.Image>

            {canUseMembership() && (
                <ProductCard.SaveForLaterButton isAffiliate={product.is_affiliate} />
            )}
            <ProductCard.Details feedType={feedType} />
        </ProductCard>
    );
}

export function getProductIdFromUrl(): number | null {
    const productId = new URLSearchParams(globals?.window?.location?.search).get("productId");
    return productId ? Number(productId) : null;
}

export function getProductIdsForCriteo(items: ProductFeedSerializer["feed_items"]): number[] {
    return items.slice(0, 3).map(({ product_card: { id } }) => id);
}

function getCuratedProductCTA(
    curatedProducts: ProductFeedSerializer["curated_products"],
    i: number
): CuratedProductsLinksSerializer | null {
    if (curatedProducts && i % 14 === 0 && curatedProducts[i / 14 - 1]) {
        return curatedProducts[i / 14 - 1];
    }
    return null;
}

interface FeedGridInnerProps {
    isFetching: boolean;
    feedItems: ProductFeedSerializer["feed_items"];
    feedType: string;
    curatedProducts?: ProductFeedSerializer["curated_products"];
}

function FeedGridInner({
    feedItems,
    isFetching,
    feedType,
    curatedProducts,
}: FeedGridInnerProps): JSX.Element {
    const criteoIds = getProductIdsForCriteo(feedItems);

    useEffect(() => {
        analytics.tagEvent(analytics.TAG_NETWORKS.CRITEO, "criteo_product_ids", {
            ids: criteoIds,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(criteoIds)]);

    const productId = getProductIdFromUrl();

    return (
        <KeylineGrid className={clsx(isFetching && styles.loading)}>
            {feedItems.map(({ product_card: product }, i) => {
                const cta = getCuratedProductCTA(curatedProducts, i);
                return (
                    <Fragment key={product.uid}>
                        {cta && (
                            <KeylineGridItem colspan={2}>
                                <CuratedProductsCard links={cta.links} />
                            </KeylineGridItem>
                        )}
                        <KeylineGridItem scrollIntoView={productId === product.id}>
                            <FeedProductCard product={product} feedType={feedType} />
                        </KeylineGridItem>
                    </Fragment>
                );
            })}
        </KeylineGrid>
    );
}
